import {
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import type { Device, SiteHost } from '../../api/types';
import { vt } from '../../theme';
import { Chip } from '../devices/DeviceTable';
import { ago } from '../overview/model';
import { reachFootnote, type Reachability } from './model';

const useStyles = makeStyles({
  head: { display: 'flex', alignItems: 'baseline', columnGap: '16px', marginBottom: '6px' },
  title: { margin: 0, fontSize: '17px', fontWeight: tokens.fontWeightSemibold, color: vt.fg1 },
  hint: { fontSize: '11.5px', color: vt.fg3 },
  table: { tableLayout: 'fixed', width: '100%' },
  headRow: { height: '30px', fontSize: '11.5px', color: vt.fg3, borderBottom: `1px solid ${vt.surface2}` },
  row: { height: '38px', borderBottom: `1px solid ${vt.surface2}` },
  cell: { paddingBlock: 0, overflow: 'hidden', whiteSpace: 'nowrap' },
  end: { textAlign: 'end', '& > *': { justifyContent: 'flex-end' } },
  // Hollow, like the map's nodes: these hosts are routable, and report nothing themselves.
  ring: {
    display: 'block',
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    border: `1.2px solid ${vt.fg4}`,
    boxSizing: 'border-box',
    marginInline: 'auto',
  },
  name: { fontSize: '13px', fontWeight: tokens.fontWeightSemibold, color: vt.fg1 },
  unnamed: { color: vt.fg3, fontWeight: tokens.fontWeightRegular },
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: '12px', color: vt.fg2 },
  faint: { fontFamily: tokens.fontFamilyMonospace, fontSize: '12px', color: vt.fg3 },
  chips: { display: 'flex', columnGap: '6px', overflow: 'hidden' },
  empty: { margin: '12px 0 0', color: vt.fg3 },
  note: { margin: '14px 0 0', fontSize: '11px', color: vt.fg3 },
});

const COLS: { key: string; label: string; width: string; end?: boolean }[] = [
  { key: 'state', label: '', width: '26px' },
  { key: 'host', label: 'Host', width: '190px' },
  { key: 'ip', label: 'LAN address', width: '132px' },
  { key: 'mac', label: 'MAC', width: '150px' },
  { key: 'reach', label: 'Reachable from', width: 'auto' },
  { key: 'seen', label: 'Last seen', width: '96px', end: true },
];

interface Props {
  site: Device;
  /** null while loading. */
  hosts: SiteHost[] | null;
  error: string | null;
  /** null until the active policy is loaded. */
  reach: Reachability | null;
  now: number;
}

/** "Behind this site": the hosts the router found on its approved routes, none with an agent. */
export function SiteHosts({ site, hosts, error, reach, now }: Props) {
  const s = useStyles();
  return (
    <section aria-label={`Hosts behind ${site.name}`}>
      <div className={s.head}>
        <h3 className={s.title}>Behind this site</h3>
        <span className={s.hint}>discovered on the approved subnets · no agent installed</span>
      </div>
      {error && <p className={s.empty}>{error}</p>}
      {!error && hosts === null && <Spinner size="tiny" label="Loading hosts" />}
      {hosts && hosts.length === 0 && (
        <p className={s.empty}>
          {site.routes.length
            ? 'No host reported yet; the router reports its DHCP leases every 15 minutes.'
            : 'Approve a route to see the hosts behind it.'}
        </p>
      )}
      {hosts && hosts.length > 0 && (
        <Table className={s.table} size="small" aria-label={`${site.name} hosts`}>
          <TableHeader>
            <TableRow className={s.headRow}>
              {COLS.map((c) => (
                <TableHeaderCell
                  key={c.key}
                  className={mergeClasses(s.cell, c.end && s.end)}
                  style={{ width: c.width }}
                  aria-label={c.key === 'state' ? 'Status' : undefined}
                >
                  {c.label}
                </TableHeaderCell>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {hosts.map((h) => (
              <TableRow key={h.ip} className={s.row} data-host={h.ip}>
                <TableCell className={s.cell}>
                  <span className={s.ring} aria-hidden />
                </TableCell>
                <TableCell className={s.cell}>
                  <span className={mergeClasses(s.name, !h.name && s.unnamed)}>{h.name ?? 'unnamed'}</span>
                </TableCell>
                <TableCell className={mergeClasses(s.cell, s.mono)}>{h.ip}</TableCell>
                <TableCell className={mergeClasses(s.cell, s.faint)}>{h.mac ?? '–'}</TableCell>
                <TableCell className={s.cell}>
                  <span className={s.chips}>
                    {reach === null ? '–' : reach.sources.length ? reach.sources.map((src) => <Chip key={src}>{src}</Chip>) : 'nothing'}
                  </span>
                </TableCell>
                <TableCell className={mergeClasses(s.cell, s.end, s.faint)}>{ago(h.lastSeenAt, now).replace(/ ago$/, '')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {reach && <p className={s.note}>{reachFootnote(reach)}</p>}
    </section>
  );
}
